import { Route, Routes } from 'react-router-dom';
import { ProtectedRoute } from './rbac/ProtectedRoute';
import { LoginPage } from './pages/Login';
import { LoginCallbackPage } from './pages/LoginCallback';
import { MfaChallengePage } from './pages/MfaChallenge';
import { NoAccessPage } from './pages/NoAccess';
import { ForbiddenPage } from './pages/Forbidden';
import { NotFoundPage } from './pages/NotFound';
import { PlaceholderPage } from './pages/Placeholder';
import { RootRedirect } from './pages/RootRedirect';
import { BrandDashboard } from './pages/BrandDashboard';
import { UsersPage } from './pages/Users';
import { ApiKeysPage } from './pages/ApiKeys';
import { AuditLogPage } from './pages/AuditLog';
import { CancelIntentsPage } from './pages/CancelIntents';
import { AppSettingsPage } from './pages/AppSettings';
import { CoreSettingsOverview } from './pages/CoreSettings';
import { TaxRatesPage } from './pages/TaxRates';
import { AdminOverviewPage } from './pages/AdminOverview';
import { NexusPluginsPage } from './pages/NexusPlugins';
import { NexusTenantRoutingPage } from './pages/NexusTenantRouting';
import { NexusSettingsPage } from './pages/NexusSettings';
import { NexusJobsPage } from './pages/NexusJobs';
import { NexusSagaReviewPage } from './pages/NexusSagaReview';
import { NexusMailSecurityPage } from './pages/NexusMailSecurity';
import { NexusPostmasterPage } from './pages/NexusPostmaster';
import { NexusUsagePage } from './pages/NexusUsage';
import { NexusCustomersPage } from './pages/NexusCustomers';
import { NexusHostingOpsPage } from './pages/NexusHostingOps';
import { DiagnosticsPage } from './pages/Diagnostics';
import { MeLayout, MeProfilePage, MeSecurityPage } from './pages/Me';
import { TenantSettingsPage } from './pages/BrandSettings';
import { OutboundWebhooksPage } from './pages/OutboundWebhooks';
import { OrdersPage } from './pages/Orders';
import { SubscriptionsPage } from './pages/Subscriptions';
import { DriversPage } from './pages/Drivers';
import { UsagePage } from './pages/Usage';
import { InfrastructurePage } from './pages/Infrastructure';
import { OssReportPage } from './pages/OssReport';
import { StripeConnectPage } from './pages/StripeConnect';
import { PolicyPage } from './pages/Policy';
import { ProductsPage } from './pages/Products';
import { TicketsPage } from './pages/Tickets';
import { IntegrationsPage } from './pages/Integrations';
import { MainShell } from '../MainShell';
import { BrandShellSync } from '../BrandShellSync';

/**
 * Single source of truth für die Route-Hierarchie.
 *
 * Mapping zur alten Core-Admin-UI siehe `nav.js`. Specs in
 * `lmm-wiki/wiki/orbit-specs/` definieren die Detail-Anforderungen pro View.
 */
export function OrbitRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/login/callback" element={<LoginCallbackPage />} />
      <Route path="/mfa-challenge" element={<MfaChallengePage />} />
      <Route path="/no-access" element={<NoAccessPage />} />
      <Route path="/403" element={<ForbiddenPage />} />

      {/* Shell-wrapped */}
      <Route element={<MainShell />}>
        <Route index element={<RootRedirect />} />

        {/* /:brandSlug/* — brand-kontextuell */}
        <Route path=":brandSlug" element={<ProtectedRoute requiresBrand />}>
          <Route element={<BrandShellSync />}>
            {/* Dashboard */}
            <Route element={<ProtectedRoute perms={['dashboard.read']} />}>
              <Route path="dashboard" element={<BrandDashboard />} />
            </Route>

            {/* Tickets — Backend: orbit.api.valitype.net (Postgres). Spec:
                lmm-wiki/wiki/orbit-specs/done/orbit-tickets.md. CORE liefert
                tickets.read seit core-tickets-permissions (2026-06-14)
                für staff+admin. */}
            <Route element={<ProtectedRoute perms={['tickets.read']} />}>
              <Route path="tickets" element={<TicketsPage />} />
              <Route path="tickets/:ticketId" element={<TicketsPage />} />
            </Route>

            {/* Users — spec: user-crud-admin */}
            <Route element={<ProtectedRoute perms={['users.read']} />}>
              <Route path="users" element={<UsersPage />} />
              <Route path="users/:userId" element={<UsersPage />} />
            </Route>

            {/* Orders & Billing */}
            <Route element={<ProtectedRoute perms={['orders.read']} />}>
              <Route path="orders" element={<OrdersPage />} />
              <Route path="orders/:orderId" element={<OrdersPage />} />
            </Route>

            <Route element={<ProtectedRoute perms={['subscriptions.read']} />}>
              <Route path="subscriptions" element={<SubscriptionsPage />} />
            </Route>

            <Route element={<ProtectedRoute perms={['cancelIntents.read']} />}>
              <Route path="cancel-intents" element={<CancelIntentsPage />} />
            </Route>

            {/* Catalog */}
            <Route element={<ProtectedRoute perms={['products.read']} />}>
              <Route path="products" element={<ProductsPage />} />
            </Route>

            <Route element={<ProtectedRoute perms={['usage.read']} />}>
              <Route path="usage" element={<UsagePage />} />
            </Route>

            {/* API-Keys */}
            <Route element={<ProtectedRoute perms={['apiKeys.read']} />}>
              <Route path="api-keys" element={<ApiKeysPage />} />
            </Route>

            {/* Integrations */}
            <Route element={<ProtectedRoute perms={['drivers.read']} />}>
              <Route path="drivers" element={<DriversPage />} />
            </Route>

            <Route element={<ProtectedRoute perms={['integrations.read']} />}>
              <Route path="integrations" element={<IntegrationsPage />} />
            </Route>

            <Route element={<ProtectedRoute perms={['stripeConnect.read']} />}>
              <Route path="stripe-connect" element={<StripeConnectPage />} />
            </Route>

            {/* Outbound-Webhooks — spec: outbound-webhooks-admin (done) */}
            <Route element={<ProtectedRoute perms={['outboundWebhooks.read']} />}>
              <Route path="outbound-webhooks" element={<OutboundWebhooksPage />} />
            </Route>

            {/* Compliance */}
            <Route element={<ProtectedRoute perms={['audit.read']} />}>
              <Route path="audit" element={<AuditLogPage />} />
            </Route>

            <Route element={<ProtectedRoute perms={['oss.read']} />}>
              <Route path="oss" element={<OssReportPage />} />
            </Route>

            <Route element={<ProtectedRoute perms={['policy.read']} />}>
              <Route path="policy" element={<PolicyPage />} />
            </Route>

            {/* Brand-Settings */}
            <Route element={<ProtectedRoute perms={['tenantSettings.read']} />}>
              <Route path="brand-settings" element={<TenantSettingsPage />} />
            </Route>
          </Route>
        </Route>

        {/* /admin/* — system-level, brandless */}
        <Route
          path="admin"
          element={<ProtectedRoute anyOf={['appSettings.read', 'integrations.read']} />}
        >
          <Route index element={<AdminOverviewPage />} />

          <Route element={<ProtectedRoute perms={['appSettings.read']} />}>
            <Route path="infra" element={<InfrastructurePage />} />
            <Route path="diagnostics" element={<DiagnosticsPage />} />
          </Route>

          <Route element={<ProtectedRoute perms={['integrations.read']} />}>
            <Route path="integrations" element={<IntegrationsPage />} />
          </Route>

          <Route path="settings">
            <Route element={<ProtectedRoute perms={['appSettings.read']} />}>
              <Route path="core" element={<CoreSettingsOverview />} />
              <Route path="core/tax-rates" element={<TaxRatesPage />} />
              <Route path="app" element={<AppSettingsPage />} />
            </Route>

            <Route element={<ProtectedRoute perms={['integrations.read']} />}>
              <Route path="nexus" element={<PlaceholderPage title="Nexus API · Settings" />} />
              <Route path="nexus/plugins" element={<NexusPluginsPage />} />
              <Route path="nexus/routing" element={<NexusTenantRoutingPage />} />
              <Route path="nexus/settings" element={<NexusSettingsPage />} />
              <Route path="nexus/jobs" element={<NexusJobsPage />} />
              <Route path="nexus/saga" element={<NexusSagaReviewPage />} />
              <Route path="nexus/mail-security" element={<NexusMailSecurityPage />} />
              <Route path="nexus/postmaster" element={<NexusPostmasterPage />} />
              <Route path="nexus/usage" element={<NexusUsagePage />} />
              <Route path="nexus/customers" element={<NexusCustomersPage />} />
              <Route path="nexus/hosting-ops" element={<NexusHostingOpsPage />} />

              <Route path="probe" element={<PlaceholderPage title="Probe API · Settings" />} />
              <Route path="probe/scrape" element={<PlaceholderPage title="Scraping Parameters" />} />
              <Route path="probe/wireguard" element={<PlaceholderPage title="WireGuard Repository" />} />
              <Route path="probe/keda" element={<PlaceholderPage title="KEDA Rules" />} />

              <Route path="pulse" element={<PlaceholderPage title="Pulse API · Settings" />} />
              <Route path="pulse/zabbix" element={<PlaceholderPage title="Zabbix Monitoring" />} />
              <Route path="pulse/telemetry" element={<PlaceholderPage title="Telemetry Endpoints" />} />
              <Route path="pulse/cpguard" element={<PlaceholderPage title="cpGuard Metrics" />} />
            </Route>
          </Route>
        </Route>

        <Route path="me" element={<ProtectedRoute />}>
          <Route element={<MeLayout />}>
            <Route index element={<MeProfilePage />} />
            <Route path="security" element={<MeSecurityPage />} />
          </Route>
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
